import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPayrollById,
  createPayroll,
  updatePayroll,
} from "../../store/thunks/payrollThunk";
import { Listbox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const ManualPayrollOverride = ({
  isOpen,
  onClose,
  appointment,
  invoices,
  payrollDates,
  payroll = null,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user || []);

  const [selectedInvoice, setSelectedInvoice] = useState(
    invoices?.length > 0 ? invoices[0] : null
  );

  console.log("received this invoices", invoices);
  console.log("selectedinvoice", selectedInvoice);

  const [fetchedPayroll, setFetchedPayroll] = useState(null);
  const [manualInvoiceAmount, setManualInvoiceAmount] = useState(
    selectedInvoice ? selectedInvoice.amount : 0
  );
  const [payPercentage, setPayPercentage] = useState(
    appointment?.clinician?.defaultPayPercentage
  );
  const [calculatedAmount, setCalculatedAmount] = useState(
    selectedInvoice ? (selectedInvoice.amount * payPercentage) / 100 : 0
  );
  const [overrideReason, setOverrideReason] = useState("");
  const [payrollStatus, setPayrollStatus] = useState("Pending");

  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (Array.isArray(invoices) && invoices.length > 0) {
      setSelectedInvoice(invoices[0]);
    }
  }, [invoices]);

  // **Fetch payroll data if only an ID is provided**
  useEffect(() => {
    if (payroll && payroll._id && Object.keys(payroll).length === 1) {
      dispatch(fetchPayrollById(payroll._id))
        .unwrap()
        .then((data) => {
          console.log("Fetched payroll data:", data);
          setFetchedPayroll(data);
        })
        .catch((error) => {
          console.error("Error fetching payroll:", error);
        });
    }
  }, [dispatch, payroll]);

  useEffect(() => {
    if (selectedInvoice) {
      setManualInvoiceAmount(selectedInvoice.amount);
    }
  }, [selectedInvoice]);

  // **Update form fields once payroll data is available**
  useEffect(() => {
    const payrollData = fetchedPayroll || payroll; // Use fetched data if available, otherwise use prop

    if (payrollData && Object.keys(payrollData).length > 1) {
      setSelectedInvoice(
        invoices.find((inv) => inv._id === payrollData.invoiceId) || invoices[0]
      );
      setManualInvoiceAmount(payrollData.originalAmount || 0);
      setPayPercentage(payrollData.percentReceived || 45);
      setCalculatedAmount(payrollData.payrollAmount || 0);
      setOverrideReason(payrollData.overrideReason || "");
      setPayrollStatus(payrollData.status || "Pending");
    } else if (invoices?.length > 0) {
      setSelectedInvoice(invoices[0]);
    }
  }, [fetchedPayroll, payroll, invoices]);

  // Recalculate payroll amount when invoice or percentage changes
  useEffect(() => {
    if (selectedInvoice) {
      setCalculatedAmount((selectedInvoice.amount * payPercentage) / 100);
      console.log("selectedinvoice", selectedInvoice);
    }
  }, [selectedInvoice, payPercentage]);

  const findMatchingPayPeriod = (payPeriodNumber) => {
    if (!payPeriodNumber) return null;

    // Convert to number if it's a string
    console.log("payPeriodNumber", payPeriodNumber);
    const periodNumber = Number(payPeriodNumber);

    // Find the period that matches exactly
    const matchingPeriod = payrollDates.find(
      (period) => Number(period.payPeriodNumber) === periodNumber
    );

    if (matchingPeriod) {
      console.log("Found matching period:", matchingPeriod);
    } else {
      console.log("No matching period found for:", periodNumber);
      console.log(
        "Available periods:",
        payrollDates.map((p) => p.payPeriodNumber)
      );
    }

    return matchingPeriod;
  };

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Update selectedPeriod when fetchedPayroll is loaded
  useEffect(() => {
    if (fetchedPayroll && Object.keys(fetchedPayroll).length > 1) {
      console.log(
        "Setting period from fetchedPayroll:",
        fetchedPayroll.payPeriod
      );
      const matchingPeriod = findMatchingPayPeriod(fetchedPayroll.payPeriod);
      if (matchingPeriod) {
        setSelectedPeriod(matchingPeriod);
      }
    } else if (payroll && Object.keys(payroll).length > 1) {
      console.log("Setting period from payroll:", payroll.payPeriod);
      const matchingPeriod = findMatchingPayPeriod(payroll.payPeriod);
      if (matchingPeriod) {
        setSelectedPeriod(matchingPeriod);
      }
    } else if (payrollDates?.length > 0) {
      setSelectedPeriod(payrollDates[0]);
    }
  }, [fetchedPayroll, payroll, payrollDates]);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  const handleInvoiceChange = (invoice) => {
    setSelectedInvoice(invoice);
    setManualInvoiceAmount(invoice ? invoice.amount : 0); // Reset manual amount to selected invoice amount
  };

  // const handleManualInvoiceAmountChange = (e) => {
  //   const amount = parseFloat(e.target.value);
  //   setManualInvoiceAmount(isNaN(amount) ? 0 : parseFloat(amount.toFixed(2)));
  // };

  // Invoice Amount Input - Allow any number with 2 decimals
  const handleManualInvoiceAmountChange = (e) => {
    const amount = parseFloat(e.target.value);
    setManualInvoiceAmount(isNaN(amount) ? 0 : parseFloat(amount.toFixed(2)));
    setCalculatedAmount((amount * payPercentage) / 100);
  };

  // Percent Received Input - Allow any number with 2 decimals
  const handlePercentageChange = (e) => {
    const percentage = parseFloat(e.target.value);
    setPayPercentage(isNaN(percentage) ? 0 : parseFloat(percentage.toFixed(2)));
    setCalculatedAmount((manualInvoiceAmount * percentage) / 100);
  };

  // Payroll Amount Input - Allow direct edits without affecting other values
  const handlePayrollAmountChange = (e) => {
    const amount = parseFloat(e.target.value);
    setCalculatedAmount(isNaN(amount) ? 0 : parseFloat(amount.toFixed(2)));
  };

  useEffect(() => {
    if (manualInvoiceAmount) {
      setCalculatedAmount((manualInvoiceAmount * payPercentage) / 100);
    }
  }, [manualInvoiceAmount, payPercentage]);

  // const handlePercentageChange = (e) => {
  //   const percentage = parseFloat(e.target.value);
  //   setPayPercentage(isNaN(percentage) ? 0 : percentage);
  // };

  const handleSubmit = async () => {
    if (!selectedInvoice) {
      setError("Please select a valid invoice.");
      return;
    }
    if (!selectedPeriod) {
      setError("Please select a valid payroll period.");
      return;
    }
    if (payPercentage === null || payPercentage === undefined) {
      setError("Pay percentage is required.");
      return;
    }
    if (!overrideReason.trim()) {
      setError("Override reason is required.");
      return;
    }

    const confirmation = window.confirm(
      payroll
        ? "You are about to update this payroll. Please ensure all fields are correct before proceeding. Do you want to continue?"
        : "You are about to manually create a payroll document. Please ensure all fields are correct before proceeding. Do you want to continue?"
    );

    if (!confirmation) {
      return; // Exit if the user cancels
    }

    setError(null); // Clear errors before submitting

    const payrollData = {
      appointmentId: appointment?._id || null,
      clinicianId: appointment?.clinician?._id || null,
      clientOrCompany: appointment?.client?._id || null,
      invoiceId: selectedInvoice._id,
      // originalAmount: selectedInvoice.amount,
      originalAmount: manualInvoiceAmount,
      percentReceived: payPercentage,
      payrollAmount: calculatedAmount,
      description: selectedInvoice.description,
      payPeriod: selectedPeriod.payPeriodNumber,
      payPeriodDates: selectedPeriod.includeDates,
      payBy: selectedPeriod.paymentDate,
      submitBy: selectedPeriod.submissionDate,
      status: payrollStatus || null,
      overrideReason: overrideReason || null,
      createdBy: user._id,
    };

    try {
      if (payroll) {
        // Update payroll
        await dispatch(
          updatePayroll({ id: payroll._id, updateData: payrollData })
        ).unwrap();
        alert("Payroll updated successfully!");
      } else {
        // Create new payroll
        await dispatch(createPayroll(payrollData)).unwrap();
        alert("Payroll created successfully!");
      }
      onClose();
    } catch (error) {
      if (error === "Payroll entry already exists for this appointment") {
        setError("Payroll entry already exists for this invoice.");
      } else {
        setError(error || "An error occurred while processing the payroll.");
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Background overlay */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white p-4 align-middle shadow-xl transition-all border-4 border-red-600">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900">
                    {payroll ? "Update Payroll" : "Manual Payroll Override"}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-sm text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    {payroll
                      ? "Manually update a payroll document."
                      : "Manually create a payroll document. An invoice must be selected."}
                  </p>
                </div>

                {/* Information Section - Full Width */}
                <div className="mt-4 space-y-1.5 text-sm">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <p className="text-gray-600">Service:</p>
                    <p className="font-medium text-gray-900">
                      {appointment?.title || "No description available"}
                    </p>
                    
                    <p className="text-gray-600">Clinician:</p>
                    <p className="font-medium text-gray-900">{`${
                      appointment?.clinician?.firstName || ""
                    } ${appointment?.clinician?.lastName || ""}`}</p>
                    
                    <p className="text-gray-600">Client:</p>
                    <p className="font-medium text-gray-900">{`${
                      appointment?.client?.firstName || ""
                    } ${appointment?.client?.lastName || ""}`}</p>
                    
                    <p className="text-gray-600">Service Date:</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(appointment?.start)}
                    </p>
                    
                    <p className="text-gray-600">Pay Period:</p>
                    <p className="font-medium text-gray-900">
                      {fetchedPayroll?.payPeriod} (
                      {fetchedPayroll?.payPeriodDates})
                    </p>
                  </div>
                </div>

                {/* Two Column Layout for Inputs */}
                <div className="mt-6 grid grid-cols-2 gap-6">
                  {/* Left Column - Selectors */}
                  <div className="space-y-4">
                    {/* Payroll Period Selection */}
                    <div>
                      <Listbox
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}>
                        <div className="flex flex-col">
                          <Listbox.Label className="block text-xs font-medium text-gray-700">
                            Select Payroll Period
                          </Listbox.Label>
                          <div className="relative mt-1">
                            <Listbox.Button className="relative w-full cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-left text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                              <span className="block truncate">
                                {`${selectedPeriod?.payPeriodNumber || ""} (${
                                  selectedPeriod?.includeDates || ""
                                })`}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                                <ChevronUpDownIcon
                                  className="h-4 w-4 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {payrollDates.map((period) => (
                                <Listbox.Option
                                  key={period.payPeriodNumber}
                                  value={period}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                                      active
                                        ? "bg-gray-50 text-gray-900"
                                        : "text-gray-900"
                                    }`
                                  }>
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={`block truncate ${
                                          selected
                                            ? "font-medium"
                                            : "font-normal"
                                        }`}>
                                        {`${period.payPeriodNumber} (${period.includeDates})`}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                          <CheckIcon
                                            className="h-4 w-4"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </div>
                        </div>
                      </Listbox>
                    </div>

                    {/* Invoice Selection */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Select Invoice
                      </label>
                      <select
                        value={selectedInvoice?._id || ""}
                        onChange={(e) => {
                          const selected = invoices?.find(
                            (inv) => inv._id === e.target.value
                          );
                          handleInvoiceChange(selected);
                        }}
                        className="mt-1 block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        disabled={!invoices?.length}>
                        {invoices?.length > 0 ? (
                          invoices.map((invoice) => (
                            <option key={invoice._id} value={invoice._id}>
                              {invoice.invoiceId || "No Description"} - $
                              {invoice.amount.toFixed(2)} - {invoice.status}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            No invoices available
                          </option>
                        )}
                      </select>
                    </div>

                    {/* Payroll Status Selection */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Select Payroll Status
                      </label>
                      <select
                        value={payrollStatus || "Select one"}
                        onChange={(e) => setPayrollStatus(e.target.value)}
                        className="mt-1 block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        {[
                          "Pending",
                          "Ready",
                          "Approved",
                          "Paid to Clinician",
                        ].map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Right Column - Amount Fields */}
                  <div className="space-y-4">
                    {/* Invoice Amount */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Invoice Amount
                      </label>
                      <div className="relative mt-1">
                        <input
                          type="number"
                          step="0.01"
                          value={manualInvoiceAmount}
                          onChange={handleManualInvoiceAmountChange}
                          placeholder="0.00"
                          className="block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Percent Received */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Percent Received
                      </label>
                      <div className="relative mt-1">
                        <input
                          type="number"
                          step="0.01"
                          value={payPercentage}
                          onChange={handlePercentageChange}
                          placeholder="0.00"
                          className="block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Payroll Amount */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Payroll Amount
                      </label>
                      <div className="relative mt-1">
                        <input
                          type="number"
                          step="0.01"
                          value={calculatedAmount}
                          onChange={handlePayrollAmountChange}
                          placeholder="0.00"
                          className="block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Override Reason - Full Width */}
                <div className="mt-6">
                  <label className="block text-xs font-medium text-gray-700">
                    Override Reason (Required)
                  </label>
                  <textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="mt-1 block w-full rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    rows="3"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mt-4 rounded-sm bg-red-50 p-2">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                    onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded border border-red-600 bg-red-600 px-2.5 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-500"
                    onClick={() => {
                      const confirmation = window.confirm(
                        payroll
                          ? "You are about to update this payroll. Please ensure all fields are correct before proceeding. Do you want to continue?"
                          : "You are about to manually create a payroll document. Please ensure all fields are correct before proceeding. Do you want to continue?"
                      );
                      if (confirmation) {
                        handleSubmit();
                      }
                    }}>
                    {payroll ? "Update Payroll" : "Create Payroll"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ManualPayrollOverride;
